import { withoutLocation } from "./location.ts"

/*
 * A photo made ready to leave the browser, wherever a Member uploads one: a ticket's or a comment's
 * file, an image in an editor, a change request's file, their own photo. An iPhone's HEIC becomes a
 * JPEG, since most browsers can't show HEIC and the server reads only JPEG, PNG and WebP; and a
 * JPEG, that one included, loses where it was taken (`withoutLocation`), since everyone who can
 * read it would otherwise learn it.
 */

/**
 * Whether a file is an iPhone's HEIC photo: by its type, or by its name where the browser gives
 * none, as some do for HEIC.
 */
export const isHeic = ({ type, name }: { type: string; name: string }) =>
  /^image\/hei[cf]$/i.test(type) || (!type && /\.hei[cf]$/i.test(name))

/** Whether a file is a JPEG, by its type, or by its name where the browser gives none. */
export const isJpeg = ({ type, name }: { type: string; name: string }) =>
  /^image\/p?jpeg$/i.test(type) || (!type && /\.jpe?g$/i.test(name))

/** A HEIC file's name once it is a JPEG: `IMG_0001.HEIC` is `IMG_0001.jpg`. A blank name stays. */
export const jpegNameOf = (name: string) => (name ? `${name.replace(/\.hei[cf]$/i, "")}.jpg` : name)

/**
 * The name a file goes up under (`uploadable`), known before it is converted, for its progress to
 * show from the start: a HEIC photo's as a `.jpg`, anything else's as it is.
 */
export const uploadedNameOf = (file: { type: string; name: string }) =>
  isHeic(file) ? jpegNameOf(file.name) : file.name

/** A HEIC photo as a JPEG. The converter is fetched only when a HEIC photo needs it. */
async function jpegOf(file: Blob) {
  const { heicTo } = await import("heic-to/csp")
  return heicTo({ blob: file, type: "image/jpeg", quality: 0.9 })
}

/** A JPEG with where it was taken taken out, or the same file when it says nothing of that. */
async function locationless(file: File) {
  const bytes = new Uint8Array(await file.arrayBuffer())
  const kept = withoutLocation(bytes)
  if (kept === bytes) return file
  return new File([kept], file.name, { type: file.type, lastModified: file.lastModified })
}

/**
 * A file as it is uploaded: a HEIC photo as a JPEG named `.jpg`, a JPEG without where it was taken,
 * and anything else as it is. Throws when a HEIC photo can't be converted, which every caller
 * refuses rather than send a photo that still says where it was taken.
 */
export async function uploadable(file: File): Promise<File> {
  if (isHeic(file)) {
    const jpeg = await jpegOf(file)
    return locationless(
      new File([jpeg], jpegNameOf(file.name), {
        type: "image/jpeg",
        lastModified: file.lastModified,
      }),
    )
  }
  return isJpeg(file) ? locationless(file) : file
}
